import { useContext, useEffect } from "react";
import debounce from "lodash/debounce";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { Button, InputGroup, NonIdealState, Tree } from "@blueprintjs/core";
import { faGhost, faSignIn } from "@fortawesome/pro-duotone-svg-icons";
import {
  faPlusCircle,
  faSearch,
  faServer,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useRegistryRules } from "@hooks";
import { Permission } from "@types";

import { SignInButton } from "@pages/editor/components/SignInButton";
import { WorkbenchContext } from "@pages/editor/providers";

const Container = styled.div<{ isPlayground: boolean }>`
  gap: var(--global-spacing-xs);
  display: flex;
  flex-direction: column;
  height: 100%;
  /*
    by default min-width: auto prevents shrinking https://stackoverflow.com/a/36247448
    min-width is (effectively) set by snap offset of Split (see workbench/Screen.tsx)

    In playground, we want to have bigger min-width to avoid colliding with browser window resize.
    Otherwise there is a small gap to show user that it is possible to resize the panel.
  */
  min-width: ${({ isPlayground }) => (isPlayground ? "8px" : "2px")};
  overflow-y: auto;
  font-size: 13px;
`;

const Header = styled.div`
  flex-shrink: 0;
  height: 64px;
  display: flex;
  padding: 0 12px;
  justify-content: space-between;
  align-items: center;
  background-color: var(--global-file-browser-purple);
  font-size: var(--global-font-size-m);
  font-weight: var(--global-font-weight-bold);
`;

const TreeContainer = styled(Tree)`
  overflow-y: auto;
  flex-grow: 1;
  & .bp3-tree-node-caret-none {
    min-width: var(--global-spacing-m);
  }
  .bp3-tree-node-content {
    height: 26px;
  }
  .bp3-tree-node {
    &.bp3-tree-node-selected > .bp3-tree-node-content {
      background-color: var(--indigo7);
      color: black;
    }
    &:not(.bp3-tree-node-selected) > .bp3-tree-node-content:hover {
      background-color: var(--light-gray4);
    }
  }
`;

const NO_RESULTS_STATE = (
  <div style={{ margin: "var(--global-spacing-l) 16px" }}>
    <NonIdealState
      icon={<FontAwesomeIcon icon={faGhost} color="var(--indigo2)" />}
      title="No matches found"
      description="Try searching for different keywords, such as “django,” “java,” or “correctness.”"
    />
  </div>
);

const LOGGED_OUT_STATE = (
  <div style={{ margin: "var(--global-spacing-l) 16px" }}>
    <NonIdealState
      icon={<FontAwesomeIcon icon={faSignIn} color="var(--indigo2)" />}
      title="Sign in required"
      description={
        <div>
          <div>
            Sign in to view and search the registry and your saved rules.
          </div>
          <div style={{ marginTop: "20px" }}>
            <SignInButton />
          </div>
        </div>
      }
    />
  </div>
);

const FileBrowserComponent = () => {
  const { workbench } = useContext(WorkbenchContext);
  const { fileRoot } = workbench;
  const { data: allRules } = useRegistryRules();
  // not logged in users can create new rules
  const canCreateNewRule =
    workbench.permissions.includes(Permission.editor_create) ||
    workbench.user === undefined;

  useEffect(() => {
    // update file root's rules whenever the rules change
    fileRoot.updateRules(allRules ?? null);
  }, [allRules, fileRoot]);

  return (
    <Container isPlayground={workbench.isPlayground}>
      <div>
        <Header className="bp3-dark">
          <div
            style={{
              flexShrink: 0,
            }}
          >
            <FontAwesomeIcon icon={faServer} /> Library
          </div>
          <Button
            outlined
            style={{ padding: "0 8px" }}
            disabled={!canCreateNewRule}
            onClick={() => workbench.newBundle()}
          >
            <FontAwesomeIcon icon={faPlusCircle} />
          </Button>
        </Header>
        <div
          style={{
            margin: "var(--global-spacing-m)",
          }}
        >
          <InputGroup
            type="search"
            style={{
              minWidth: "0",
              height: "36px",
            }}
            fill
            leftElement={
              <FontAwesomeIcon
                style={{
                  color: "var(--gray1)",
                  margin: "12px 8px",
                }}
                icon={faSearch}
              />
            }
            placeholder="e.g.: python.flask"
            disabled={fileRoot.workbench.user === undefined}
            onChange={debounce(
              (e) => fileRoot.updateSearchQuery(e.target.value),
              300
            )}
          />
        </div>
      </div>{" "}
      {fileRoot.isSearching && !fileRoot.matchesSearchQuery ? (
        NO_RESULTS_STATE
      ) : fileRoot.workbench.user === undefined ? (
        LOGGED_OUT_STATE
      ) : (
        <TreeContainer contents={fileRoot.childrenBlueprintNodes} />
      )}
    </Container>
  );
};

export const FileBrowser = observer(FileBrowserComponent);
